import { IconButton, Stack, TextField, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TuneIcon from '@mui/icons-material/Tune';
import { useState } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { CrudPage } from '../../shared/components/CrudPage';
import { resources } from '../../core/services/resources';
import type { Project } from '../../core/types/data-models';
import { ProjectLlmSettingsDialog } from './ProjectLlmSettingsDialog';

const PROJECT_TYPE_LABELS: Record<Project['projectType'], string> = {
  IDEA_VALIDATION: 'Idea Validation',
  BUSINESS_VALIDATION: 'Business Validation',
  STOCK_VALIDATION: 'Stock Validation',
  MARKET_VALIDATION: 'Market Validation',
  SECTOR_VALIDATION: 'Sector Validation',
  STRATEGY_MANAGEMENT: 'Strategy Management',
};

export function ProjectsPage() {
  const navigate = useNavigate();
  const [llmSettingsTarget, setLlmSettingsTarget] = useState<Project | null>(null);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 220,
      renderCell: (p) => (
        <RouterLink to={`/projects/${p.row.id}`}>{p.value as string}</RouterLink>
      ),
    },
    {
      field: 'projectType',
      headerName: 'Type',
      width: 180,
      valueGetter: (value: Project['projectType']) => PROJECT_TYPE_LABELS[value] ?? value,
    },
    { field: 'description', headerName: 'Description', flex: 2, minWidth: 240 },
    {
      field: 'tenantName',
      headerName: 'Tenant',
      width: 180,
      valueGetter: (_value, row) => row.tenant?.name ?? '—',
    },
    {
      field: 'documentCount',
      headerName: 'Documents',
      type: 'number',
      width: 120,
      valueGetter: (_value, row) => row._count?.documents ?? 0,
    },
  ];

  return (
    <>
      <CrudPage<Project>
        title="Projects"
        entityName="Project"
        description="Tenant-scoped containers. Open one to manage its notes."
        cacheKey={['projects']}
        service={resources.projects}
        include={['tenant', '_count']}
        columns={columns}
        defaultValues={{ name: '', description: '' }}
        rowActions={(row) => (
          <>
            <Tooltip title="LLM model settings">
              <IconButton size="small" onClick={() => setLlmSettingsTarget(row)} aria-label="llm model settings">
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open project">
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/projects/${row.id}`)}
                aria-label="open project"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        renderForm={({ values, setValues }) => (
          <Stack spacing={2}>
            <TextField
              label="Name"
              required
              value={values.name ?? ''}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
            />
            <TextField
              label="Description"
              multiline
              minRows={2}
              value={values.description ?? ''}
              onChange={(e) => setValues({ ...values, description: e.target.value })}
            />
          </Stack>
        )}
      />

      <ProjectLlmSettingsDialog
        open={llmSettingsTarget !== null}
        project={llmSettingsTarget}
        onClose={() => setLlmSettingsTarget(null)}
      />
    </>
  );
}
